/**
 * What build am I looking at, and is it the current one?
 *
 * Those are two different questions, and the semver only answers the first. It
 * moves when someone remembers to move it, so `v1.0.0` says nothing about
 * whether a newer deploy exists. The commit sha answers the second, because it
 * can be compared against master — which is why `compareUrl()` exists and why
 * the Settings row is a button rather than a label.
 *
 * `__APP_VERSION__` and `__BUILD_SHA__` are compile-time constants from
 * `define` in vite.config.ts. Because `define` substitutes them literally at
 * transform time they cannot be stubbed in a test, so the logic lives in the
 * two pure functions below and the module-level bindings are thin wrappers.
 */

const REPO_URL = 'https://github.com/aspandyar/manager';

/** Enough sha to identify a commit by eye and to paste into `git show`. */
const SHORT_LEN = 7;

/** `v1.0.0 · 4c042d4`, or `v1.0.0 · dev` when no commit backs the build. */
export function formatVersion(version: string, sha: string): string {
  return `v${version} · ${sha ? sha.slice(0, SHORT_LEN) : 'dev'}`;
}

/**
 * GitHub's compare view, which literally answers "am I behind?" — it lists the
 * commits master has that this build does not, and says so in a sentence at the
 * top. An empty comparison means you are on the newest code.
 *
 * `null` when there is no sha: a dev build has no commit to compare against,
 * and a link that 404s is worse than no link.
 */
export function buildCompareUrl(sha: string): string | null {
  return sha ? `${REPO_URL}/compare/${sha}...master` : null;
}

export const APP_VERSION = __APP_VERSION__;
export const BUILD_SHA = __BUILD_SHA__;

/**
 * A dev server, or a build made somewhere without git. Worth distinguishing:
 * "is this the latest?" is not a meaningful question about a bundle that was
 * never released, and answering it with a version number would imply it was.
 */
export const isDevBuild = BUILD_SHA === '';

export function versionLabel(): string {
  return formatVersion(APP_VERSION, BUILD_SHA);
}

export function compareUrl(): string | null {
  return buildCompareUrl(BUILD_SHA);
}
